import type { InputProps } from '@breetometer/components';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';
import type { pageMenuIconType } from 'BreetAssets';
import type { accessCredentialsModeType, JsonObject } from 'BreetComponents';
import type { ModalConfigIdType, ModalConfigStateType } from 'BreetConfig';
import {
	type ComponentPropsWithoutRef,
	type CSSProperties,
	type ElementType,
	type FC,
	type JSX,
	type ReactNode,
	type RefObject,
	type SVGProps,
} from 'react';
import type { FieldError, FieldPath, PathValue, UseFormProps, UseFormRegisterReturn, UseFormReturn } from 'react-hook-form';
import type * as yup from 'yup';

import type {
	COUNTRYTYPE,
	CURRENCYCODETYPE,
	CURRENCYSHORTTYPE,
	CURRENCYSYMBOLTYPE,
	FXCONVERSION,
	FXRATE,
	KYBSTATUSES,
} from '../constants';
import type { NewDeviceLoginSchema, PinVerificationSchema } from '../data';

export type SVGComponentProps = SVGProps<SVGSVGElement>;

export const imageFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'heic'] as const;

export type ImageFileType = (typeof imageFileTypes)[number];

export type DocumentFileType =
	| 'pdf'
	| 'doc'
	| 'docx'
	| 'xls'
	| 'xlsx'
	| 'ppt'
	| 'pptx'
	| 'txt'
	| 'csv'
	| 'rtf'
	| 'odt'
	| 'html'
	| 'md';

export type AudioFileType = 'mp3' | 'wav' | 'ogg' | 'flac' | 'aac';
export type VideoFileType = 'mp4' | 'mov' | 'avi' | 'mkv' | 'webm' | 'wmv';
export type ArchiveFileType = 'zip' | 'rar' | '7z' | 'tar' | 'gz';
export type ExecutableFileType = 'exe' | 'apk' | 'dmg' | 'iso';

export type FileType = ImageFileType | DocumentFileType | AudioFileType | VideoFileType | ArchiveFileType | ExecutableFileType;

export type FormData<L extends yup.ObjectSchema<yup.AnyObject>> = yup.InferType<L>;

export interface IUseFormData<L extends yup.ObjectSchema<yup.AnyObject>> {
	hookFormProps: UseFormReturn<FormData<L>>;
	getFieldError: (fieldPath: FieldPath<yup.InferType<L>>) => FieldError | undefined;
	isFormCompleted: boolean;
	setValueAndValidate: <TFieldPath extends FieldPath<FormData<L>>>(
		fieldPath: TFieldPath,
		value: PathValue<FormData<L>, TFieldPath>
	) => Promise<void>;
	clearCache: () => void;
}

export interface IUseFormDataOptions<L extends yup.ObjectSchema<yup.AnyObject>> extends Pick<
	UseFormProps<FormData<L>>,
	'mode' | 'reValidateMode'
> {
	cache?: {
		key: string;
		promptOnUnload?: boolean;
		cacheOnUnload?: boolean;
		cacheOnRouteChange?: boolean;
		onBeforeUnload?: () => void;
	};
}

export interface IMeta {
	settings: ISettings;
	hasNextPage: boolean;
	hasPrevPage: boolean;
	nextPage: number | null;
	page: number;
	pages: number;
	prevPage: number | null;
	totalDocs: number;
}

export interface IApiResponse<R = object> {
	success: boolean;
	message: string;
	data?: R;
	meta?: IMeta;
	summary?: Record<string, { total?: number; totalAmount?: number }>;
	stats?: Record<string, Partial<{ count: number; rating: number; collection: string }>[]>;
}

export interface IKybDirector {
	firstName: string;
	lastName: string;
	fullHomeAddress: string;
	recentAddressProof: string;
	meansOfID: string;
}

export interface IKybAbout {
	companyName: string;
	country: string;
	businessDescription: string;
	officialEmail: string;
	taxId: string;
	officialTelephone: string;
	proofOfAddress: string;
	evidenceOfRegistration: string;
	memart: string;
	statusReport: string;
	extraDoc?: string;
}

export interface IKybDetails extends IKybAbout {
	token: string;
	directors: IKybDirector[];
	acknowledged: boolean;
	status: KybStatusType | undefined;
}

export interface IKybCredentials {
	email: string;
	token: string;
}

export interface IKybDocDetails {
	name: string;
	type: string;
	image: File;
}

export interface IKybDocResponse {
	name: string;
	type: string;
	image: {
		0: object;
	};
	url: string;
	imageUrl: string;
}

export interface IApiResponseError {
	status: number;
	data: {
		success: boolean;
		message: string;
		errors?: IApiResponseValidationErrorErrors[];
	};
}
export interface IApiResponseValidationErrorErrors {
	message: string;
	field: string;
	location: string;
}

export type KybStatusType = (typeof KYBSTATUSES)[keyof typeof KYBSTATUSES];

export interface PreviewFileMeta {
	type: string;
	previewUrl: string;
	fileSelectorId: string;
}

export interface TypographyProps {
	as?: ElementType;
	variant: 'big_header' | 'mid_header' | 'small_header' | 'sub_head' | 'paragraph_1' | 'paragraph_2' | 'paragraph_3';
	children?: ReactNode;
}

export type KybFormFileType = FileList | string | undefined;

export interface CountryProps {
	code: string;
	name: string;
	dialCode: string;
	flag: string;
}
export interface Breadcrumb {
	label: string;
	url: string;
}

export interface IFiatMetaProps {
	currency: CURRENCYCODETYPE;
	currencyLower: Lowercase<CURRENCYCODETYPE>;
	currencySymbol: CURRENCYSYMBOLTYPE;
	country: COUNTRYTYPE;
	countryLower: Lowercase<COUNTRYTYPE>;
	currencyShort: CURRENCYSHORTTYPE;
}

export type accessCredentialsStatusType = 'idle' | 'processing' | 'verified';

export type accessCredentialsVerificationIdType =
	| 'request-payment'
	| 'add-beneficiary'
	| 'delete-beneficiary'
	| 'automatic-settlement'
	| 'withdraw-request'
	| 'redeem-points'
	| 'reset-pin'
	| '2fa'
	| '2fa-step'
	| 'generate-webhook-secret'
	| 'generate-api-secret'
	| 'set-webhook-url'
	| 'set-ip-address'
	| 'edit-ip-address'
	| 'set-markup-percentage'
	| 'edit-wallet-address-name'
	| 'login'
	| '';

export type accessCredentialsVerificationStatusType = 'idle' | 'processing' | 'verified';

export type accessCredentialsVerificationType = 'verify' | 'collect';

export interface ICountDownStartArray {
	id: string;
	startTime: number;
}

export interface IAccessCredentialEvent {
	verificationId: string;
	code: string;
}

export type walletBalanceType = 'USD' | 'NGN' | 'GHS';

export interface AppState {
	themeMode: 'dark' | 'light';
	loggingIn: { email: string; password: string } | null;
	isUserLoggedIn: boolean;
	kybCredentials?: IKybCredentials;
	fiatMeta: IFiatMetaProps;
	meta?: IMeta;
	wallet: IUserWallet[];
	beneficiaries: IBeneficiary[];
	preference: IUserPreference | undefined;
	accessCredentialEvents: IAccessCredentialEvent[];
	userData: IUser | undefined;
	mobileMenuOpen?: boolean;
	selectedBeneficiary?: IBeneficiary;
	authSettings?: IAuthSettings;
	countDownStartTimeArray: ICountDownStartArray[];
	lastVisited?: string;
	isTokenFound?: boolean;
	latestNotification?: NotificationPayload | null;
	fcmToken?: string;
	isFiatBalanceHidden?: boolean;
	showSecretKey?: boolean;
	is2faAlert?: boolean;
}

export interface ISettingsFXRates {
	NGNToGHS: number;
	NGNToUSD: number;
	NGNToUSDForVIP: number;
}
export interface ISettingsBillsMeta {
	categories: Record<string, IBillsMeta>;
	providers: Record<string, boolean>;
	trading: boolean;
}

export interface IAuthSettingsTrading {
	enabled: boolean;
	message: string;
	minimum: number;
}
export interface IAuthSettingsVIPTrading {
	enabled: boolean;
	message: string;
	minimum: number;
}
export interface IAuthSettingsVIP {
	trading: IAuthSettingsVIPTrading;
	rates: Record<string, number>;
	alertLimit: number;
	cbr: number;
}

export interface IAuthSettingsSellMeta {
	trading: IAuthSettingsTrading;
	rates: Record<string, number>;
	alertLimit: number;
	cbr: number;
	vip: IAuthSettingsVIP;
}
export interface IAuthSettingsSwapMeta {
	trading: {
		enabled: boolean;
		message: string;
	};
	rates: Record<string, number>;
}
export interface IAuthSettingsInvoiceMetaTrading {
	enabled: boolean;
	message: string;
	minimum: number;
	minimumAmount: Record<Lowercase<CURRENCYCODETYPE>, number>;
}
export interface IAuthSettingsInvoiceMeta {
	trading: IAuthSettingsInvoiceMetaTrading;
	rates: Record<string, number>;
}
export interface IAuthSettingsKYCProviders {
	platform: 'web' | 'android' | 'ios';
	providers: {
		dojah: true;
		youverify: false;
	};
}

export type IAuthSettingsSupportLineKey =
	| 'callLine'
	| 'whatsAppLine'
	| 'whatsAppLink'
	| 'telegramLink'
	| 'instagramLink'
	| 'xLink'
	| 'email';

export interface IAuthSettingsSupportLineProps {
	isEnabled: boolean;
	value: string;
}

export type IAuthSettingsBillMetaCategoriesType = 'airtime' | 'betting' | 'broadband' | 'data' | 'electricity' | 'tv';
export interface IAuthSettingsBillMetaCategories {
	amount: number;
	discountType: string;
	isDiscounted: boolean;
	isEnabled: boolean;
	max?: number;
	message: string;
	min?: number;
}
export type IAuthSettingsUSDWithdrawalMetaFees = Record<TokenType, Record<NetworkType, number>>;
export interface IAuthSettingsUSDWithdrawalMeta {
	enabled: boolean;
	message: string;
	minimum: number;
	maximum: number;
	dailyLimits: {
		noKYC: number;
		basicKYC: number;
	};
	autoPayment: boolean;
	providers: {
		manual: boolean;
		fireblocks: boolean;
	};
	fees: IAuthSettingsUSDWithdrawalMetaFees;
	tokens: Record<TokenType, { enabled: boolean }>;
	networks: Record<NetworkType, { enabled: boolean }>;
	vip: {
		enabled: boolean;
		message: string;
		minimum: number;
		maximum: number;
		dailyLimits: {
			noKYC: number;
			basicKYC: number;
		};
		autoPayment: boolean;
		providers: {
			manual: boolean;
			fireblocks: boolean;
		};
		fees: IAuthSettingsUSDWithdrawalMetaFees;
		tokens: Record<TokenType, { enabled: boolean }>;
		networks: Record<NetworkType, { enabled: boolean }>;
	};
}

export interface IAuthSettingsWithdrawalMeta {
	providers: {
		ghana: {
			manual: boolean;
			startbutton: boolean;
		};
		nigeria: {
			monnify: boolean;
			redbiller: boolean;
			palmpay: boolean;
			manual: boolean;
		};
	};
	withdrawal: {
		enabled: Record<Lowercase<COUNTRYTYPE>, boolean>;
		message: string;
		autoSettlement: Record<Lowercase<COUNTRYTYPE>, boolean>;
		minima: {
			nigeria: number;
			ghana: number;
			us: number;
		};
		maximum: {
			nigeria: number;
			ghana: number;
			us: number;
		};
		dailyLimits: {
			noKYC: Record<Lowercase<COUNTRYTYPE>, number>;
			basicKYC: Record<Lowercase<COUNTRYTYPE>, number>;
		};
		autoPayment: Record<Lowercase<COUNTRYTYPE>, boolean>;
	};
	vip?: {
		providers?: {
			ghana: {
				manual: boolean;
				startbutton: boolean;
			};
			nigeria: {
				monnify: boolean;
				redbiller: boolean;
				palmpay: boolean;
				manual: boolean;
				startbutton: boolean;
			};
		};
		withdrawal?: {
			enabled: Record<Lowercase<COUNTRYTYPE>, boolean>;
			message: string;
			minimum: number;
			autoSettlement: Record<Lowercase<COUNTRYTYPE>, boolean>;
			minima: Record<Lowercase<COUNTRYTYPE>, number>;
			dailyLimits: {
				minimum: Record<Lowercase<COUNTRYTYPE>, number>;
				maximum: Record<Lowercase<COUNTRYTYPE>, number>;
			};
			autoPayment: Record<Lowercase<COUNTRYTYPE>, boolean>;
		};
	};
}

export interface IAuthSettingsPlatformFeeProps {
	min: number;
	max: number;
	rate: number;
}

interface IAuthSettingsCurrencySwapMeta {
	enabled: boolean;
	NGN: {
		minimum: number;
	};
	GHS: {
		minimum: number;
	};
}
export interface IAuthSettings {
	_id: string;
	fxRates: ISettingsFXRates & { buy: ISettingsFXRates };
	sellMeta: IAuthSettingsSellMeta;
	swapMeta: IAuthSettingsSwapMeta;
	currencySwapMeta: IAuthSettingsCurrencySwapMeta;
	invoiceMeta: IAuthSettingsInvoiceMeta;
	billsMeta: {
		categories: Record<IAuthSettingsBillMetaCategoriesType, IAuthSettingsBillMetaCategories>;
		providers: {
			v2n: boolean;
			redbiller: boolean;
		};
		trading: boolean;
		rates: Record<string, number>;
	};
	kycMeta: {
		providers: {
			dojah: boolean;
			youverify: boolean;
		};
		enable: {
			advance: boolean;
			basic: boolean;
		};
		kycProviders: IAuthSettingsKYCProviders[];
	};
	transferMeta: {
		transfer: {
			enabled: Record<Lowercase<COUNTRYTYPE>, boolean>;
			message: string;
			minimum: {
				nigeria: number;
				ghana: number;
				us: number;
			};
			maximum: {
				nigeria: number;
				ghana: number;
				us: number;
			};
		};
		vip: {
			transfer: {
				enabled: Record<Lowercase<COUNTRYTYPE>, boolean>;
				message: string;
				minimum: {
					nigeria: number;
					ghana: number;
					us: number;
				};
				maximum: {
					nigeria: number;
					ghana: number;
					us: number;
				};
			};
		};
	};
	rewardMeta: {
		signupPoints: number;
		referralReward: number;
		rewardPerDollar: number;
		redeemLimit: {
			ngn: number;
			ghs: number;
		};
		vipRedeemLimit: {
			ngn: number;
			ghs: number;
		};
		levelThreshold: {
			ngn: Record<string, number>;
			ghs: Record<string, number>;
		};
	};
	conversionMeta: {
		USD: {
			enabled: boolean;
			message: string;
			minimum: number;
			rates: Record<string, number>;
		};
	};
	supportLine: Record<IAuthSettingsSupportLineKey, IAuthSettingsSupportLineProps>;
	createdAt: string;
	updatedAt: string;
	appVersionDetails: {
		ios: string;
		android: string;
		message: string;
		isCompulsory: boolean;
	};
	platformFeePercent: IAuthSettingsPlatformFeeProps[];
	platformFeePercentVip: IAuthSettingsPlatformFeeProps[];
	anomalyThresholds: {
		singleUserSellAmount: number;
		dailySellCountPerUser: number;
		dailySellVolumePerUser: number;
		totalSellCountPerDay: number;
		dailySwapCountPerUser: number;
		totalSwapCountPerDay: number;
		dailyInvoiceCountPerUser: number;
		dailyPartiallyPaidInvoiceCountPerUser: number;
		singleUserInvoiceAmount: number;
		dailyBillCountPerUser: number;
		dailyBillVolumePerUser: number;
		totalBillVolumePerDay: number;
		singleUserWithdrawalAmount: number;
		dailyWithdrawalCountPerUser: number;
		dailyWithdrawalVolumePerUser: number;
		totalWithdrawalVolumePerDay: number;
		totalWithdrawalCountPerDay: number;
		totalFailedWithdrawalCountPerHour: number;
		totalNewUsersCountPerDay: number;
		totalDeletedUsersCountPerDay: number;
		singleRewardPointIssuedAmount: number;
		singleRewardPointRedeemedAmount: number;
	};
	withdrawalMeta?: IAuthSettingsWithdrawalMeta;
	usdWithdrawalMeta?: IAuthSettingsUSDWithdrawalMeta;
}

export interface ISettings {
	_id: string;
	billsMeta: ISettingsBillsMeta;
	fxRates?: ISettingsFXRates;
	invoiceMeta: {
		rates: ISellAssetRateType;
		trading: { message: string; minimum: number; enabled: boolean; minimumAmount: Record<string, number> };
	};
	currencySwapMeta: IAuthSettingsCurrencySwapMeta;
	kycMeta: {
		enable: { basic: boolean; advance: boolean };
		kycProviders: { platform: string; providers: Record<string, boolean> }[];
		providers: Record<string, boolean>;
	};
	swapMeta: {
		trading: { message: string; enabled: boolean };
		rates: Record<string, number>;
	};
	conversionMeta: {
		USD: {
			message: string;
			enabled: boolean;
			minimum: number;
			rates: { currency: Lowercase<CURRENCYCODETYPE>; value: number }[];
		};
	};
	sellMeta: {
		alertLimit: number;
		cbr: number;
		rates: Record<string, number>;
		trading: { message: string; minimum: number; enabled: boolean };
		vip: {
			alertLimit: number;
			cbr: number;
			rates: Record<string, number>;
			trading: { message: string; minimum: number; enabled: boolean };
		};
	};
	withdrawalMeta: {
		vip: {
			providers:
				| Record<string, boolean>
				| {
						ghana: Record<string, boolean>;
						nigeria: Record<string, boolean>;
				  };
			withdrawal: {
				enabled: Record<Lowercase<COUNTRYTYPE>, boolean>;
				message: string;
				minimum: number;
				autoSettlement: Record<Lowercase<COUNTRYTYPE>, boolean>;
				minima: Record<Lowercase<COUNTRYTYPE>, number>;
				fee: Record<Lowercase<COUNTRYTYPE>, number>;
				dailyLimits: {
					minimum: Record<Lowercase<COUNTRYTYPE>, number>;
					maximum: Record<Lowercase<COUNTRYTYPE>, number>;
				};
				autoPayment: Record<Lowercase<COUNTRYTYPE>, boolean>;
				maximum: Record<Lowercase<COUNTRYTYPE>, number>;
			};
		};
		withdrawal: {
			message: string;
			enabled: Record<Lowercase<COUNTRYTYPE>, boolean>;
			autoPayment: Record<Lowercase<COUNTRYTYPE>, boolean>;
			autoSettlement: Record<Lowercase<COUNTRYTYPE>, boolean>;
			minima: Record<string, number>;
			maximum: Record<string, number>;
			dailyLimits: {
				noKYC: Record<Lowercase<COUNTRYTYPE>, number>;
				basicKYC: Record<Lowercase<COUNTRYTYPE>, number>;
			};
		};
		_id: string;
	};
	usdWithdrawalMeta?: IAuthSettingsUSDWithdrawalMeta;
	rewardMeta: {
		redeemLimit: Record<Lowercase<CURRENCYCODETYPE>, number>;
		vipRedeemLimit: Record<Lowercase<CURRENCYCODETYPE>, number>;
		signupPoints: number;
		referralReward: number;
		rewardPerDollar: number;
		levelThreshold: Record<Lowercase<CURRENCYCODETYPE>, Record<string, number>>;
	};
	supportLine: {
		callLine: ISupportLineMeta;
		whatsAppLine: ISupportLineMeta;
		whatsAppLink: ISupportLineMeta;
		telegramLink: ISupportLineMeta;
		instagramLink: ISupportLineMeta;
		xLink: ISupportLineMeta;
		email: ISupportLineMeta;
	};
	appVersionDetails: {
		ios: string;
		android: string;
		message: string;
		isCompulsory: boolean;
	};
	createdAt: string;
	updatedAt: string;
}

export type NetworkType = 'TRC20' | 'ERC20' | 'BSC' | 'SOL';

export type TokenType = 'USDT' | 'USDC';

export interface IWalletInfoValue {
	avatar: string;
	networks: {
		network: NetworkType;
		avatar: string;
	}[];
}
export type IWalletInfo = Record<TokenType, IWalletInfoValue>;

export interface IBillsMeta {
	amount: number;
	discountType: 'flat' | 'percentage';
	isDiscounted: boolean;
	isEnabled: boolean;
	max: number;
	message: string;
	min: number;
}

export interface ISellAssetRateType {
	0: number;
	51: number;
	201: number;
	1001: number;
	5001: number;
}

export interface ISupportLineMeta {
	isEnabled: boolean;
	value: string;
}

interface IUserShortDetails {
	_id: string;
	name: string;
	email: string;
	username: string;
	country?: Lowercase<COUNTRYTYPE>;
}

export interface IUserTagType {
	note: string;
	status: boolean;
	taggedAt?: string;
	admin?: IUserShortDetails;
}

export interface IUser {
	_id: string;
	level: number;
	freeze: boolean;
	isVerified: boolean;
	tag: IUserTagType;
	verifications: {
		hasBVN: boolean;
	};
	apiAccess?: boolean;
	ssid?: string;
	role: string;
	platformFeePercent?: number;
	avatar: string;
	passwordUpdatedAt: string;
	phoneUpdatedAt: string;
	suspended: boolean;
	email: string;
	phoneNumber: string;
	businessDescription?: string;
	username: string;
	country: string;
	device: string;
	referralCode: string;
	createdAt: string;
	updatedAt: string;
	lastLogin: string;
	fcmTokens: {
		v: string;
		k?: string;
	}[];
	kyc: {
		advance: boolean;
		basic: boolean;
	};
	// vip
	name: string;
	isVip: boolean;
	shouldChangeSubscriptionTopic: boolean;
	wallet: IUserWallet;
	preference: IUserPreference;
}

export type ReceiveCurrencyType = Lowercase<CURRENCYCODETYPE> | 'card';
export interface IUserPreference {
	_id: string;
	user: string;
	is2FAActive: boolean;
	isAuthenticator2FaActive: boolean;
	autoSettlement: boolean;
	enableBiometrics: boolean;
	enablePushNotifications: boolean;
	enableEmailNotifications: boolean;
	receiveCurrency?: ReceiveCurrencyType;
	createdAt: string;
	updatedAt: string;
	setups?: {
		activatePin: boolean;
		activateKYC: boolean;
		activateBank: boolean;
	};
	dashboardFavorites: string[];
	enablePN: boolean;
	autoSettlementBank: null;
	autoSettlementBankInfo: null;
	unusedRecoveryCodesCount: number;
	usedRecoveryCodesCount: number;
}

export interface UpdatePreferenceTypes {
	activateBank?: boolean;
	activateKYC?: boolean;
	activatePin?: boolean;
	dashboardFavorites?: number[];
	receiveCurrency?: ReceiveCurrencyType;
	enableMarketRateNotification?: boolean;
}

export interface IBank {
	id: string;
	hasLogo: boolean;
	name: string;
	slug: string;
	redbillerCode: string;
	anchorCode: string;
	monnifyCode: string;
	palmpayCode: string;
	country: string;
	currency: CURRENCYCODETYPE;
	type: string;
	avatar: string;
}

export interface IUserWallet {
	balance: number;
	createdAt: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	meta?: Record<string, string>;
	type?: 'fiat' | 'crypto' | 'reward';
	updatedAt: string;
	userInfo?: IUserShortDetails;
	_id: string;
	user: string;
	isActive: boolean;
	// new props
	totalPoints?: 0;
	totalRedeemed?: 0;
	totalRefPoints?: 0;
	totalSellPoints?: 0;
	stats: {
		count: 0;
		totalUSD: 0;
		totalCrypto: 0;
		totalFiat: 0;
	};
}

export interface ICurrencyConfig {
	country: string;
	locale: string;
	symbol: string;
	currency: string;
	currencyShort: string;
	code: string;
}

export interface IFxRates {
	NGNToGHS: number;
	NGNToUSD: number;
}

export interface IValidateBankDetails {
	accountNumber: string;
	type: string;
	bankName: string;
	accountName: string;
}

export interface IBeneficiary {
	_id: string;
	createdAt: string;
	updatedAt: string;
	type: string;
	bankId: string;
	avatar: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	accountNumber: string;
	autoSettlement: boolean;
	bankName: string;
	accountName: string;
	user: string;
	disabled: boolean;
	account: string;
	narration?: string;
	userInfo: {
		_id: string;
		name: string;
		email: string;
		username: string;
		country: Lowercase<COUNTRYTYPE>;
	};
}

export interface IResponseStatus {
	onClose?: () => void;
	title?: string;
	subTitle?: string;
	buttonTitle?: string;
}

export interface ICheckbox extends Omit<ComponentPropsWithoutRef<'input'>, 'placeholder' | 'accept'> {
	register?: UseFormRegisterReturn;
	errorMsg?: string;
	children?: ReactNode;
}

export interface IDropDownItem {
	code: string;
	name: string;
	value: string;
	id?: string;
	icon: JSX.Element | string;
}

export interface IPasswordInput extends Omit<InputProps, 'id'> {
	id?: string;
	showPassword?: boolean;
}

export interface ModalProps {
	modalId: ModalConfigIdType;
	header?: ReactNode;
	manualClose?: boolean;
	closeBtnRef?: RefObject<HTMLButtonElement>;
	onClose?: (_state?: boolean) => void;
	wrapperClassName?: string;
	dialogMessage?: string;
	variant?: 'right-sheet' | 'default';
	hideCloseButton?: boolean;
}

interface IBankDetails {
	accountName: string;
	bankName: string;
	accountNumber: string;
}

export interface ITransactionDetailsRow {
	label: string;
	value?: string;
	bankDetails?: IBankDetails;
}

export interface IUseAccessCredentialsRequest {
	onSuccess?: () => void;
	onError: (error?: unknown) => void;
	code?: string;
}
export interface IAccessCredentialComponentProps {
	mode: accessCredentialsModeType;
	description: string;
	title: string;
	type: accessCredentialsVerificationType;
	dispatchAccessCredentials: (code?: string) => void;
	inputLength?: number;
	generateOTP?: boolean;
	refetchFn?: () => Promise<void>;
	resendOtp?: boolean;
	buttonTitle?: string;
}

export interface IUseAccessCredentialsArg {
	mode: accessCredentialsModeType;
	submitRequest: (data?: IUseAccessCredentialsRequest) => Promise<void> | void;
	id: accessCredentialsVerificationIdType;
	type: accessCredentialsVerificationType;
	description?: string;
	title?: string;
	dialogMessage?: string;
	closeModalId?: ModalConfigIdType;
	persistOnVerify?: boolean;
	render?: (props: IAccessCredentialComponentProps) => ReactNode;
	inputLength?: number;
	generateOTP?: boolean;
	refetchFn?: () => Promise<void>;
	resendOtp?: boolean;
	buttonTitle?: string;
}

export interface IUseAccessCredentials {
	handleVerification: () => void;
	onSuccess: () => void;
	onError: (errorMessageArg?: string) => void;
	mode: accessCredentialsModeType;
	description: string;
	title: string;
	submitRequest: (data?: IUseAccessCredentialsRequest) => Promise<void> | void;
	type: accessCredentialsVerificationType;
	component?: ReactNode;
	buttonTitle?: string;
}

export type beneficiaryListModeType = 'delete' | 'select';

export interface IBeneficiaryList {
	beneficiaries?: IBeneficiary[];
	handleBeneficiarySelection: (beneficiary: IBeneficiary, mode: beneficiaryListModeType) => () => void;
	selectedBeneficiaryId?: string;
	hideHeader?: boolean;
	mode?: beneficiaryListModeType;
	title?: string;
}

export interface IActivityLog {
	createdAt: string;
	description: string;
}

export interface ISellAssetsRates {
	0: number;
	51: number;
	201: number;
	1001: number;
	5001: number;
}
export interface ISellVIPAssetsRates {
	0: number;
	51: number;
	201: number;
	1001: number;
	5001: number;
}
export interface ISellAssetsStats {
	count: number;
	totalUSD: number;
	totalUSDSells: number;
	totalFiat: number;
	totalCrypto: number;
	_id: string;
	price: number;
}

export interface ISellAssetsMarketQuoteValue {
	price: number;
	volume_24h: number;
	volume_change_24h: number;
	percent_change_1h: number;
	percent_change_24h: number;
	percent_change_7d: number;
	percent_change_30d: number;
	percent_change_60d: number;
	percent_change_90d: number;
	market_cap: number;
	market_cap_dominance: number;
	fully_diluted_market_cap: number;
	tvl: null;
	last_updated: string;
}
export type ISellAssetsMarketQuote = Record<CURRENCYCODETYPE, ISellAssetsMarketQuoteValue>;
export interface ISellAssetsMarketAsset {
	isActive: boolean;
	type: string;
	contractAddress: string;
	nativeAsset: string;
	id: string;
	symbol: string;
	legacyTicker: string;
	identifier: string;
	name: string;
	isAccountBased: boolean;
	decimals: number;
	confirmations: number;
	txLink: string;
}
export interface ISellAssetsMarket {
	id: number;
	name: string;
	symbol: string;
	slug: string;
	num_market_pairs: number;
	date_added: string;
	tags: string[];
	max_supply: number;
	circulating_supply: number;
	total_supply: number;
	infinite_supply: boolean;
	platform: null;
	cmc_rank: number;
	self_reported_circulating_supply: null;
	self_reported_market_cap: null;
	tvl_ratio: null;
	last_updated: string;
	quote: ISellAssetsMarketQuote;
	asset: ISellAssetsMarketAsset;
	avatar: string;
}

export interface ISellAssetWalletData {
	address: string;
	legacyAddress: string;
	tag: string;
	bip44AddressIndex: number;
}
export interface ISellAssetWallet {
	_id: string;
	createdAt: string;
	updatedAt: string;
	avatar: string;
	vaultId: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	total: number;
	balance: number;
	isActive: boolean;
	data: ISellAssetWalletData;
	asset: string;
	name: string;
	type: string;
	user: string;
	identifier: string;
	address: string;
	isAccountBased: boolean;
	isVip: boolean;
	userInfo: IUserShortDetails;
	qr: string;
}
export interface ISellAssets {
	_id: string;
	id: string;
	symbol: string;
	legacyTicker: string;
	identifier: string;
	isActive: boolean;
	name: string;
	type: string;
	contractAddress: string;
	nativeAsset: string;
	isAccountBased: boolean;
	decimals: number;
	confirmations: number;
	txLink: string;
	minimum: number;
	createdAt: string;
	updatedAt: string;
	avatar: string | JSX.Element;
	rates: ISellAssetsRates;
	minimumVaultBalance: number;
	sweepEnabled: boolean;
	flagFeeUSD: number;
	vipMinimum: number;
	vipRates: ISellVIPAssetsRates;
	wallet: ISellAssetWallet | null;
	stats: ISellAssetsStats;
	market: ISellAssetsMarket;
}

export interface IUserReferral {
	_id: string;
	createdAt: string;
	updatedAt: string;
	userInfo: IUserShortDetails;
	user: string;
	points: number;
	totalRefPoints: number;
	convertedUsers: number;
	isConverted: boolean;
	referredUsersCount: number;
	referralCode: string;
}

export interface IVIPStatsWithdraw {
	totalCount: number;
	completedAmount: number;
	completedCount: number;
	processingAmount: number;
	processingCount: number;
	pendingAmount: number;
	pendingCount: number;
	reversedAmount: number;
	reversedCount: number;
}
export interface IVIPStatsSell {
	totalCount: number;
	totalUsdAmount: number;
	totalNairaAmount: number;
	successfulTrades: number;
	pendingTrades: number;
	unsucessfulTrades: number;
	flaggedTrades: number;
}
export interface IVIPStats {
	sellsAnalytics: IVIPStatsSell[];
	withdrawalsAnalytics: IVIPStatsWithdraw[];
}

export interface IBreadcrumbs {
	noHome?: boolean;
	pageTitle?: string;
	activeLinkUrl?: string;
}

export interface DateRange {
	startDate: string;
	endDate: string;
}

export interface INotification {
	_id: string;
	createdAt: string;
	updatedAt: string;
	read: boolean;
	isPromotional: boolean;
	message: string;
	subject: string;
	readAt: string;
	label: string;
	expiresAt: string;
}

export type ParamType = Record<string, string | number | boolean | undefined> | undefined;

export interface WindowSize {
	width: number;
	height: number;
}

export type IAppStatus = 'successful' | 'processing' | 'pending' | 'reversed' | 'unsuccessful' | 'flagged' | boolean;

export type ITransactionStatus = 'completed' | 'pending' | 'flagged' | 'deleted' | 'processing' | 'reversed';

export interface ITransactionHistoryDocAssetDocRate {
	0: number;
	51: number;
	201: number;
	1001: number;
	5001: number;
}
export interface ITransactionHistoryDocAssetVIPRates {
	0: number;
	51: number;
	201: number;
	1001: number;
	5001: number;
}
export interface ITransactionHistoryDocAssetDoc {
	_id: string;
	id: string;
	symbol: string;
	legacyTicker: string;
	identifier: string;
	isActive: boolean;
	name: string;
	type: string;
	contractAddress: string;
	nativeAsset: string;
	isAccountBased: boolean;
	decimals: number;
	confirmations: number;
	txLink: string;
	minimum: number;
	createdAt: string;
	updatedAt: string;
	avatar: string;
	rates: ITransactionHistoryDocAssetDocRate;
	minimumVaultBalance: number;
	sweepEnabled: boolean;
	flagFeeUSD: number;
	vipMinimum: number;
	vipRates: ITransactionHistoryDocAssetVIPRates;
}
export interface ITransactionHistoryDocDataSource {
	id: string;
	type: string;
	name: string;
	subType: string;
}
export interface ITransactionHistoryDocDestination {
	id: string;
	type: string;
	name: string;
	subType: string;
}
export interface ITransactionHistoryDocDataAmountInfo {
	amount: string;
	requestedAmount: string;
	netAmount: string;
	amountUSD: string;
}
export interface ITransactionHistoryDocDataBlockInfo {
	blockHeight: string;
	blockHash: string;
}
export interface ITransactionHistoryDocData {
	id: string;
	createdAt: number;
	lastUpdated: number;
	assetId: string;
	source: ITransactionHistoryDocDataSource;
	destination: ITransactionHistoryDocDestination;
	amount: number;
	networkFee: number;
	netAmount: number;
	sourceAddress: string;
	destinationAddress: string;
	destinationAddressDescription: string;
	destinationTag: string;
	status: Uppercase<ITransactionStatus>;
	txHash: string;
	subStatus: Uppercase<ITransactionStatus>;
	signedBy: unknown[];
	createdBy: string;
	rejectedBy: string;
	amountUSD: number;
	addressType: string;
	note: string;
	exchangeTxId: string;
	requestedAmount: number;
	feeCurrency: string;
	operation: string;
	customerRefId: null;
	numOfConfirmations: number;
	amountInfo: ITransactionHistoryDocDataAmountInfo;
	feeInfo: object;
	destinations: unknown[];
	externalTxId: null;
	blockInfo: ITransactionHistoryDocDataBlockInfo;
	signedMessages: unknown[];
	index: number;
	assetType: string;
}
export interface ITransactionHistorySellDoc {
	_id: string;
	reference: string;
	address: string;
	amount: number;
	amountReceived: number;
	asset: string;
	assetDoc: ITransactionHistoryDocAssetDoc;
	avatar: string;
	cbr: number;
	balanceBefore: number;
	balanceAfter: number;
	conversionRate: number;
	createdAt: string;
	cryptoReceived: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	data: ITransactionHistoryDocData;
	feeAmount: number;
	flagFeeUSD: number;
	identifier: string;
	isAccountBased: boolean;
	isCompletedWithFee?: boolean;
	isVip: boolean;
	minimum: number;
	name: string;
	passAMLCheck: boolean;
	platformFeePercent: number;
	pointsEarned?: number;
	rate: number;
	reason: string;
	roundedAmountReceived: number;
	resolvedBy?: string;
	sourceAddress: string;
	status: ITransactionStatus;
	sweepStatus: boolean;
	txHash: string;
	txLink: string;
	updatedAt: string;
	user: string;
	userInfo: IUserShortDetails;
	vaultId: string;
	wallet: string;
	walletCredited: boolean;
	balanceRemaining: number;
}

export interface ITransactionHistoryEditsDoc {
	_id: string;
	createdAt: string;
	updatedAt: string;
	amount: number;
	reason: string;
	isCredit: boolean;
	user: string;
	status: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	balanceAfter: number;
	balanceBefore: number;
	userInfo: IUserShortDetails;
	admin: {
		email: string;
		username: string;
		_id: string;
	};
}

export interface ITransactionHistoryWithdrawalsDoc {
	_id: string;
	createdAt: string;
	updatedAt: string;
	meta: IWithdrawalsHistoryMeta & IWithdrawalsHistoryMetaAddress;
	amount: number;
	log: string;
	provider: string;
	reason: string;
	admin: string | null;
	user: string;
	fee: number;
	sessionId: string;
	reversedBy: string | null;
	channel: string;
	reference: string;
	account: string;
	autoSettlement: boolean;
	status: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	balanceAfter: number;
	balanceBefore: number;
	userInfo: IUserShortDetails;
}

export interface ITransactionHistoryConversionsDoc {
	_id: string;
	user: string;
	userInfo: IUserShortDetails;
	channel: string;
	from?: Lowercase<CURRENCYCODETYPE>;
	to?: Lowercase<CURRENCYCODETYPE>;
	isBuyCrypto?: boolean;
	rate: number;
	conversionRate: number;
	amount: number;
	usdBefore: number;
	usdAfter: number;
	fiatBefore: number;
	fiatAfter: number;
	meta: object;
	status: string;
	swapBalances?: {
		ngnBefore: number;
		ngnAfter: number;
		ghsBefore: number;
		ghsAfter: number;
	};
}

export type ITransactionHistoryType = 'withdrawals' | 'edits' | 'sells' | 'conversions';

export interface ITransactionHistory<T extends { _id: string }> {
	_id: string;
	collection: ITransactionHistoryType;
	docId: string;
	amount: number;
	conversionRate: number;
	createdAt: string;
	currency: Lowercase<CURRENCYCODETYPE>;
	description: string;
	doc: T;
	isCredit: boolean;
	isExcluded: boolean;
	isSettled: boolean;
	status: ITransactionStatus;
	updatedAt: string;
	user: string;
	rating?: number;
}

export type PrimitiveValue = string | number | boolean;

export interface DropdownOptionType extends Record<string, PrimitiveValue> {
	id: string;
	label: string;
	value: PrimitiveValue;
}

export interface IWithdrawalsHistoryMeta {
	type?: 'nuban' | 'address' | 'ghs_bank';
	bankId?: string;
	bankName?: string;
	accountName?: string;
	account?: string;
	accountNumber?: string;
	autoSettlement?: boolean;
	narration?: string;
	fee?: number;
}
export interface IWithdrawalsHistoryMetaAddress {
	walletAddress: string;
	network: NetworkType;
	token: TokenType;
	assetId: string;
	type: string;
	symbol: string;
	avatar: string;
	label: string;
	txLink: string;
}
export interface IWithdrawalsHistory {
	_id: string;
	createdAt: string;
	updatedAt: string;
	meta?: IWithdrawalsHistoryMeta;
	amount: number;
	log: string;
	provider: string;
	reason: string;
	admin: null;
	user: string;
	sessionId: string;
	reversedBy: null;
	channel: string;
	reference: string;
	account: string;
	autoSettlement: boolean;
	status: ITransactionStatus;
	currency: Lowercase<CURRENCYCODETYPE>;
	balanceAfter: number;
	balanceBefore: number;
	userInfo: IUserShortDetails;
}

export interface IRewardPointsMeta {
	actualDollarAmount: number;
	rewardPerDollar: number;
}
export interface IRewardPoints {
	_id: string;
	createdAt: string;
	updatedAt: string;
	points: number;
	conversionRate: number;
	user: string;
	isCredit: boolean;
	benefactor: null;
	source: string;
	status: string;
	doc: string;
	description: string;
	userInfo: IUserShortDetails;
	meta: IRewardPointsMeta;
}

export interface BankSelectProps extends Omit<InputProps, 'onChange' | 'label'> {
	defaultBank?: string;
	label?: string;
	onChange?: (valueArg: string) => void;
}
export interface DropdownSelectProps extends Omit<InputProps, 'onChange' | 'label'> {
	defaultOption?: string;
	label?: string;
	onChange?: (valueArg: string) => void;
	options: IDropDownItem[];
	placeHolder?: string;
	hideSearch?: boolean;
	CustomItemOption?: FC<{ item: IDropDownItem }>;
}

export interface CountrySelectProps extends Omit<InputProps, 'onChange' | 'label'> {
	defaultCountry?: string;
	label?: string;
	onChange?: (valueArg: string) => void;
	hideSearch?: boolean;
}

export type windowSizeType = WindowSize & {
	isClient: boolean;
};

export interface IFlashMessage {
	_id?: string;
	type?: string;
	createdAt?: string;
	message?: string;
	severity?: 'soft' | 'persistent';
	time?: string;
	updatedAt?: string;
	isActive?: boolean;
	expiresAt: string;
	isVip: boolean;
}

export interface IWalletProps {
	currencyAmount: string;
	walletBalance: number;
	walletId: string;
	name: string;
	email: string;
	symbol: string;
	code: string;
}

export interface IUseFormStageNavigator<T extends string> {
	next: () => void;
	prev: () => void;
	reset: () => void;
	goTo: (i: number | T) => () => void;
	historyBack: () => void;
	historyForward: () => void;
}

export interface IUseFormStage<T extends string> {
	stage: T;
	navigator: IUseFormStageNavigator<T>;
	isStageInactive: (stageArg: T) => boolean;
	isStageActive: (stageArg: T) => boolean;
	stageTitle: string | Record<T, string>[T];
	history: T[];
}

export interface IBeneficiariesProps {
	onAddNewBeneficiary?: () => void;
	onManageBeneficiaries?: () => void;
	title?: string;
	selectBeneficiary: (beneficiary: IBeneficiary | undefined) => void;
	selectedBeneficiaryId?: string;
	mode?: beneficiaryListModeType;
}

export interface UseCurrencyOutputOptions {
	value?: string | number;
	roundTo?: number;
	minimumRoundNum?: number;
	conversionRate?: number;
	returnUndefined?: boolean;
	roundMode?: 'floor' | 'ceil' | 'round';
}

export interface IFeatureNote {
	_id: string;
	feature: 'sell' | 'swap' | 'invoice' | 'wallet' | 'bills';
	type: string;
	createdAt: string;
	isActive: boolean;
	message: string;
	updatedAt: string;
	isForVip?: boolean;
	coin?: string;
}

export interface IGetMarketAssetConverter {
	id: number;
	symbol: string;
	name: string;
	amount: number;
	last_updated: string;
	quote: Record<
		CURRENCYCODETYPE,
		{
			price: number;
			last_updated: string;
		}
	>;
}

export type PinVerificationSchemaType = yup.InferType<typeof PinVerificationSchema>;

export type formPinModeType = 'create' | 'update' | 'reset';

export interface ISelectedBeneficiary {
	accountName: string;
	accountNumber: string;
	bankName: string;
	id: string;
	autoSettlement?: boolean;
}

export interface ApiFulfilledAction<TData = unknown, TMeta = unknown> {
	type: string;
	payload: TData;
	meta?: TMeta;
}

export type SettingsAction = ApiFulfilledAction<{
	meta?: {
		settings?: IAuthSettings;
	};
}>;

export interface AuthSettingsPayload {
	meta?: {
		settings?: IAuthSettings;
	};
}

export type UploadErrorPayload =
	| {
			status: number;
			data: string;
	  }
	| {
			status: 'FETCH_ERROR';
			data: undefined;
			error: string;
	  };

export type UploadQueryReturnValue = QueryReturnValue<void, UploadErrorPayload, undefined>;

export interface IUploadPresignedDocArg {
	url: string;
	file: File | Blob;
	contentType: string;
}

export interface InputTestProps {
	otpLength?: number;
	onChange?: (val: string) => void;
}

export interface VersionData {
	version?: string;
	buildTime?: string;
	timestamp?: string | number;
	[key: string]: unknown;
}

type StorageType = 'local' | 'session' | 'none';

export type versionCheckType = 'polling' | 'route-change';

export type versionCheckActionType = 'reload-page' | 'prompt-user';

export interface VersionCheckOptions {
	versionFile?: string;
	checkInterval?: number;
	onNewVersion?: (versionCheckAction?: versionCheckActionType) => void;
	versionCheckType?: versionCheckType;
	storage?: StorageType;
	throttleMs?: number;
	timeUnit?: 'seconds' | 'minutes';
}

export interface NotificationPayload {
	title?: string;
	body?: string;
	data?: object;
}

export interface IFCMNotificationPayload {
	notification?: { title: string; body: string };
	data?: { title?: string; body?: string };
	messageId?: string;
}

export interface ITabProps<T extends string> {
	activeTab: T;
	setActiveTab: (tabItem: T) => void;
	isActiveTab: (tabItem: T) => boolean;
}

export interface IWalletAddress {
	_id: string;
	createdAt: string;
	updatedAt: string;
	user: string;
	address: string;
	network: NetworkType;
	token: TokenType;
	label: string;
	avatar: string;
}

export interface IUseVerifyTransaction {
	handleVerification: () => void;
	userPreferenceData?: IApiResponse<IUserPreference>;
	userPreferenceLoading: boolean;
}

export interface ISaveWalletAddress {
	_id: string;
	user: string;
	address: string;
	network: NetworkType;
	token: TokenType;
	label: string;
	avatar: string;
}

export interface IUseVerifyTransactionProps {
	submitRequest: (data?: IUseAccessCredentialsRequest, handleCallback?: () => void) => Promise<void> | void;
	closeModalId?: ModalConfigIdType;
	description?: string;
	refetchOTP?: () => Promise<void>;
}
export interface IManageUSDOptions {
	receiveCurrency: ReceiveCurrencyType;
	onSuccess?: () => void;
}
export interface IUseCryptoSettingsApi {
	manageUSD: (options: IManageUSDOptions) => void;
	currentReceiveCurrency?: ReceiveCurrencyType;
	isLoading: boolean;
	isFetching: boolean;
	fiatMeta: IFiatMetaProps;
	isUpdating: boolean;
}
export interface IDropDownChildArgs {
	buttonRef: RefObject<HTMLButtonElement | null>;
	handleVisibility: () => void;
	visible: boolean;
	dropdownRef: RefObject<HTMLDivElement | null>;
	controlRef: RefObject<HTMLDivElement | HTMLButtonElement | HTMLParagraphElement | null>;
	dropdownStyles: CSSProperties | undefined;
	tipPlacement: string;
}

export type TypeDropDownChild = (args: IDropDownChildArgs) => ReactNode;

export interface IDeveloperIntegration {
	_id: string;
	createdAt: string;
	updatedAt: string;
	user: string;
	userInfo: {
		_id: string;
		email: string;
		username: string;
		country: string;
	};
	webhook?: {
		url?: string;
		secret?: string;
	};
	ipAddresses?: string[];
	isActive: boolean;
	merchantReference: string;
	markupPercent?: string;
	appId: string;
	name: string;
}

export interface IDeveloperIntegrationAPISecret {
	appSecret: string;
}
export interface IDeveloperIntegrationWebhookAPISecret {
	webhookSecret: string;
}

export interface IPageMenuItem {
	variant: pageMenuIconType;
	title: string;
	description: string;
	path: string;
	hide?: boolean;
	external?: boolean;
	handleMenuClick?: () => void;
}

export interface ISettingsMenuItem extends IPageMenuItem {
	loading?: boolean;
}

export interface ISubAccountTradeWalletAddresses {
	id: string;
	avatar: string;
	name: string;
	label: string;
	address: string;
	identifier: string;
	asset: string;
	currency: string;
	vaultId: string;
	type: string;
	isAccountBased: boolean;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export type TransactionHistoryType = ITransactionHistoryEditsDoc &
	ITransactionHistoryWithdrawalsDoc &
	ITransactionHistoryConversionsDoc;

export interface IPagination {
	totalDocItems?: number;
	pageLimit: number;
	siblingCount?: number;
	currentPage: number;
}

export interface IWalletInfoOptionProps {
	avatar: string;
	networks: {
		network: NetworkType;
		avatar: string;
	}[];
	token: TokenType;
}

export type IWebhookLogsEventType =
	| 'withdrawal.pending'
	| 'withdrawal.completed'
	| 'withdrawal.processing'
	| 'trade.completed'
	| 'trade.pending'
	| 'trade.flagged';

export interface IWebhookLogsAttempts {
	attemptedAt: string;
	responseStatus: number;
	responseBody: string;
}
export type IWebhookLogsStatusType = 'delivered' | 'failed';
export interface IWebhookLogsProps {
	_id: string;
	eventType: IWebhookLogsEventType;
	reference: string;
	integration: string;
	attempts: IWebhookLogsAttempts[];
	attemptsCount: number;
	createdAt: string;
	deliveredAt: string;
	expireAt: string;
	lastAttemptAt: string;
	payload: JsonObject;
	status: IWebhookLogsStatusType;
	updatedAt: string;
	url: string;
	id: string;
}

export type FXRATE_TYPE = (typeof FXRATE)[keyof typeof FXRATE];

export type FXCONVERSION_TYPE = (typeof FXCONVERSION)[keyof typeof FXCONVERSION];

export type TRANSACTION_TYPE = 'withdraw-fiat' | 'convertFiatToUSD' | 'withdrawUSD' | 'swapCurrency';

export interface IUseCurrentFXRate {
	rate?: string;
	rateInfo: string;
	symbol: Record<'from' | 'to', CURRENCYSYMBOLTYPE>;
	rateInfoSymbol: Record<'from' | 'to', CURRENCYSYMBOLTYPE>;
}

export interface IUseWalletBalance {
	getFiatWalletByType: (type: walletBalanceType) => IUserWallet | undefined;
	getBalance: (fiat: walletBalanceType) => string;
	fiatWallet?: IUserWallet;
	wallets?: IUserWallet[];
	isWalletBalanceLoading: boolean;
	isWalletBalanceFetching: boolean;
	isWalletBalanceError: boolean;
}

export interface IUseModalApi {
	handleModalClose: (modalId: ModalConfigIdType, onClose?: () => void) => () => void;
	closeModalDirect: (modalId: ModalConfigIdType) => void;
	handleOpenModal: (payload: ModalConfigStateType, onOpen?: () => void) => () => void;
	isOpenModal: (modalId: ModalConfigIdType) => boolean;
	openModalDirect: (payload: ModalConfigStateType) => {
		payload: ModalConfigStateType;
		type: 'modals/openModal';
	};
	handleModalCloseAll: () => void;
	getModalProps: (modalId: ModalConfigIdType) => ModalConfigStateType | undefined;
}

export type NewDeviceLoginSchemaType = yup.InferType<typeof NewDeviceLoginSchema>;

export type OTPTYPE = 'authenticator' | 'recoveryCode' | 'email';

export interface IRequestPaymentReqData {
	bankId: string;
	amount: number;
	pin?: string;
	narration?: string;
	otp?: TwoFaType;
}

export interface IConvertFiatToUsdReqData {
	withdrawalAddressId?: string;
	amount: number;
	pin?: string;
	otp?: TwoFaType;
}

export interface IWithdrawUsdReqData {
	walletId: string;
	amount: number;
	pin: string;
	otp?: TwoFaType;
}

export interface ISwapCurrencyReqData {
	amount: number;
	pin: string;
	from: Lowercase<CURRENCYCODETYPE>;
	to: Lowercase<CURRENCYCODETYPE>;
	otp?: TwoFaType;
}

export interface TwoFaType {
	code: string;
	type: OTPTYPE;
}

export interface IInitiateAuthenticator2Fa {
	secret: string;
	provisioningUri: string;
}

export interface IActivateAuthenticator2fa {
	recoveryCodes: string[];
}

export interface IOTPCTA {
	message?: string;
	action?: () => void;
	btnTitle?: string;
}
